/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio ZorgMail Inbox API
 * The practice\'s own ZorgMail Secure e-mail mailbox, read inside Scrivio (ADR-050).  ZorgMail is the source of truth and Scrivio holds a disposable cache of it, kept in step by a sync every two minutes. Reads answer from that cache. A **state change** — read, unread, to the Prullenbak — is applied on the ZorgMail server first and only then in the cache; when the server refuses or cannot be reached, nothing changes and the error says why.  Read state is the server\'s `\\Seen` flag and is **shared by the whole practice**. Opening a message does not set it: the client asks for it with `markZorgMailInboxMessageSeen`, so a preview does not mark mail read for everyone.  Every message may concern any cliënt, so the inbox has a permission of its own, `zorgmail_inbox.read`, granted by default to the practice\'s Administrator only. Sending in the practice\'s name is a separate risk and a separate permission, `zorgmail_inbox.send`.  ## Authentication All endpoints require a Firebase Authentication JWT token in the `Authorization: Bearer <token>` header, and answer for the caller\'s active organisation only.  ## Error Responses All error responses follow RFC 7807 Problem Details format. A failure talking to ZorgMail carries a stable `code` extension — see `ZorgMailInboxProblemCode`. 
 *
 * The version of the OpenAPI document: 1.0.0
 * Contact: support@scrivio.nl
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

export interface FileZorgMailInboxMessageRequest {
    /**
     * The cliënt whose dossier the message is filed into
     */
    'clientId': string;
    /**
     * The `index` of each attachment to file beside the message; empty files the message alone
     */
    'attachmentIndexes': Array<number>;
}
/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}
/**
 * At most one of `inReplyTo` and `forwardOf`.
 */
export interface SendZorgMailInboxMessageRequest {
    'to': Array<string>;
    'cc'?: Array<string>;
    'subject': string;
    /**
     * Plain text, written by the user; a quoted or forwarded original follows it
     */
    'body': string;
    /**
     * The cached message this answers
     */
    'inReplyTo'?: string;
    /**
     * The cached message this forwards
     */
    'forwardOf'?: string;
    /**
     * Which of `forwardOf`\'s attachments go along, by their `index`
     */
    'forwardedAttachmentIndexes'?: Array<number>;
    /**
     * Documents from one cliënt\'s dossier to attach
     */
    'documentIds'?: Array<string>;
}
export interface SendZorgMailInboxMessageResponse {
    /**
     * The `Message-ID` the message went out with
     */
    'messageId'?: string;
    /**
     * Whether the copy reached Verzonden. False means the message **was sent** but is not in the practice\'s Verzonden folder — \"verzonden, maar niet in Verzonden opgeslagen\". 
     */
    'savedInSent': boolean;
}
export interface ZorgMailInboxAddress {
    'address': string;
    'name'?: string;
}
export interface ZorgMailInboxAttachment {
    /**
     * What `downloadZorgMailInboxAttachment` takes
     */
    'index': number;
    'filename'?: string;
    /**
     * The attachment\'s declared base type, lower case — as the sender stated it
     */
    'contentType': string;
    /**
     * The decoded size
     */
    'sizeBytes'?: number;
    /**
     * Shown in the body rather than offered as a file
     */
    'inline': boolean;
}
export interface ZorgMailInboxFiling {
    'id': string;
    'clientId': string;
    /**
     * The document holding the PDF of the message itself
     */
    'messageDocumentId': string;
    /**
     * One document per filed attachment, in index order
     */
    'attachmentDocumentIds': Array<string>;
    /**
     * Epoch milliseconds
     */
    'filedAt': number;
}
export interface ZorgMailInboxFolder {
    'id': string;
    'role': ZorgMailInboxFolderRole;
    /**
     * The folder\'s name on the server, e.g. `Verzonden`
     */
    'name': string;
    'unreadCount': number;
    'totalCount': number;
    /**
     * When the sync last brought this folder up to date (epoch milliseconds)
     */
    'lastSyncedAt': number;
}


export interface ZorgMailInboxFolderListResponse {
    'folders': Array<ZorgMailInboxFolder>;
    /**
     * Whether the practice\'s mailbox login is stored. Without one there is nothing to sync.
     */
    'mailboxConfigured': boolean;
    'lastSyncError'?: ZorgMailInboxSyncError;
}


/**
 * Which folder this is, found by its special-use role rather than by name
 */

export const ZorgMailInboxFolderRole = {
    Inbox: 'INBOX',
    Sent: 'SENT',
    Trash: 'TRASH'
} as const;

export type ZorgMailInboxFolderRole = typeof ZorgMailInboxFolderRole[keyof typeof ZorgMailInboxFolderRole];


export interface ZorgMailInboxMessageDetail {
    'id': string;
    'folderId': string;
    'from'?: ZorgMailInboxAddress;
    'to': Array<ZorgMailInboxAddress>;
    'cc': Array<ZorgMailInboxAddress>;
    'subject'?: string;
    'sentAt'?: number;
    'receivedAt'?: number;
    'seen': boolean;
    'flagged': boolean;
    /**
     * The plain-text body. Derived from the HTML body when the message has no text part, and empty when it has neither. 
     */
    'textBody': string;
    /**
     * The HTML body, sanitised. Absent when the message has no HTML part.
     */
    'htmlBody'?: string;
    'attachments': Array<ZorgMailInboxAttachment>;
}
export interface ZorgMailInboxMessagePage {
    'items': Array<ZorgMailInboxMessageSummary>;
    'page': number;
    'size': number;
    'totalElements': number;
    'totalPages': number;
}
export interface ZorgMailInboxMessageSummary {
    'id': string;
    'folderId': string;
    'from'?: ZorgMailInboxAddress;
    'subject'?: string;
    /**
     * The sender\'s `Date:` header (epoch milliseconds)
     */
    'sentAt'?: number;
    /**
     * When ZorgMail received it (epoch milliseconds)
     */
    'receivedAt'?: number;
    /**
     * The server\'s `\\Seen` flag — read for the whole practice
     */
    'seen': boolean;
    'flagged': boolean;
    'hasAttachments': boolean;
    'sizeBytes'?: number;
}
/**
 * The stable `code` extension on a ZorgMail inbox problem
 */

export const ZorgMailInboxProblemCode = {
    ZorgmailInboxNotConfigured: 'zorgmail-inbox-not-configured',
    ZorgmailInboxMessageGone: 'zorgmail-inbox-message-gone',
    ZorgmailInboxNoTrashFolder: 'zorgmail-inbox-no-trash-folder',
    ZorgmailInboxAlreadyInTrash: 'zorgmail-inbox-already-in-trash',
    ZorgmailInboxAuthenticationFailed: 'zorgmail-inbox-authentication-failed',
    ZorgmailInboxUnreachable: 'zorgmail-inbox-unreachable',
    ZorgmailInboxProtocolError: 'zorgmail-inbox-protocol-error',
    ZorgmailInboxAttachmentInfected: 'zorgmail-inbox-attachment-infected',
    ZorgmailInboxAttachmentScanUnavailable: 'zorgmail-inbox-attachment-scan-unavailable',
    ZorgmailInboxMessageTooLarge: 'zorgmail-inbox-message-too-large',
    ZorgmailInboxRecipientRefused: 'zorgmail-inbox-recipient-refused',
    ZorgmailInboxRecipientNotPermitted: 'zorgmail-inbox-recipient-not-permitted',
    ZorgmailInboxSendRefused: 'zorgmail-inbox-send-refused',
    ZorgmailInboxAlreadyFiled: 'zorgmail-inbox-already-filed'
} as const;

export type ZorgMailInboxProblemCode = typeof ZorgMailInboxProblemCode[keyof typeof ZorgMailInboxProblemCode];


/**
 * Why the last sync of the mailbox failed: ZorgMail refused the login, could not be reached, the secure connection failed, the server answered something unexpected, or the sync failed otherwise. 
 */

export const ZorgMailInboxSyncError = {
    AuthenticationFailed: 'AUTHENTICATION_FAILED',
    Unreachable: 'UNREACHABLE',
    TlsFailed: 'TLS_FAILED',
    ProtocolError: 'PROTOCOL_ERROR',
    Failed: 'FAILED'
} as const;

export type ZorgMailInboxSyncError = typeof ZorgMailInboxSyncError[keyof typeof ZorgMailInboxSyncError];



/**
 * ZorgmailInboxApi - axios parameter creator
 */
export const ZorgmailInboxApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Served only once the malware scan has called it clean (ADR-038). An infected attachment is refused with `zorgmail-inbox-attachment-infected`; when no verdict can be had the scan fails closed with `zorgmail-inbox-attachment-scan-unavailable`.  Always `application/octet-stream` with an attachment disposition; the attachment\'s own content type is on `ZorgMailInboxAttachment`. Every download is recorded in the access log. 
         * @summary One attachment\'s content
         * @param {string} messageId A cached message\&#39;s id
         * @param {number} index The attachment\&#39;s &#x60;index&#x60; on &#x60;ZorgMailInboxMessageDetail&#x60;
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadZorgMailInboxAttachment: async (messageId: string, index: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'messageId' is not null or undefined
            assertParamExists('downloadZorgMailInboxAttachment', 'messageId', messageId)
            // verify required parameter 'index' is not null or undefined
            assertParamExists('downloadZorgMailInboxAttachment', 'index', index)
            const localVarPath = `/api/v1/zorgmail-inbox/messages/{messageId}/attachments/{index}`
                .replace(`{${"messageId"}}`, encodeURIComponent(String(messageId)))
                .replace(`{${"index"}}`, encodeURIComponent(String(index)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/octet-stream,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * \"Koppel aan cliënt\" (ADR-050 §6): copies the message into the chosen cliënt\'s dossier as a PDF of its headers and body, and each chosen attachment as a document of its own. The cliënt is the caller\'s choice; nothing is suggested. The message stays in the inbox.  Every file is type-checked from its bytes and malware-scanned before any is stored, so a refusal files nothing: an infected file is `zorgmail-inbox-attachment-infected`, no scan verdict is `zorgmail-inbox-attachment-scan-unavailable`. A message already filed into this cliënt\'s dossier is refused with `zorgmail-inbox-already-filed`.  Needs `zorgmail_inbox.read` and access to the cliënt\'s dossier. 
         * @summary File a message into a cliënt\'s dossier
         * @param {string} messageId A cached message\&#39;s id
         * @param {FileZorgMailInboxMessageRequest} fileZorgMailInboxMessageRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        fileZorgMailInboxMessage: async (messageId: string, fileZorgMailInboxMessageRequest: FileZorgMailInboxMessageRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'messageId' is not null or undefined
            assertParamExists('fileZorgMailInboxMessage', 'messageId', messageId)
            // verify required parameter 'fileZorgMailInboxMessageRequest' is not null or undefined
            assertParamExists('fileZorgMailInboxMessage', 'fileZorgMailInboxMessageRequest', fileZorgMailInboxMessageRequest)
            const localVarPath = `/api/v1/zorgmail-inbox/messages/{messageId}/file`
                .replace(`{${"messageId"}}`, encodeURIComponent(String(messageId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(fileZorgMailInboxMessageRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The headers, a plain-text body and — when the message has one — an HTML body that has been **sanitised on the server**: scripts, event handlers, forms, frames and styles that load anything are removed, and links open outside the app.  An image is never loaded from the sender\'s server — that is a read receipt and an IP leak. A remote image is replaced by an `<img data-zorgmail-blocked=\"remote\">` placeholder that has no `src`. An image the message carries itself (`cid:`) becomes `<img data-zorgmail-attachment=\"{index}\">`, which the client fills in from `downloadZorgMailInboxAttachment` with that index; a `cid:` that names no attachment is blocked like a remote one.  The first open downloads the message from ZorgMail. Opening does **not** mark it read. Every open is recorded in the access log. 
         * @summary One message, with its body
         * @param {string} messageId A cached message\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getZorgMailInboxMessage: async (messageId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'messageId' is not null or undefined
            assertParamExists('getZorgMailInboxMessage', 'messageId', messageId)
            const localVarPath = `/api/v1/zorgmail-inbox/messages/{messageId}`
                .replace(`{${"messageId"}}`, encodeURIComponent(String(messageId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * INBOX, Verzonden and Prullenbak, as far as the sync has cached them. Empty for a practice whose mailbox has not been synced yet, or has no mailbox login — which `mailboxConfigured` tells apart. `lastSyncError` is absent while the last sync succeeded. 
         * @summary The cached folders, with their unread counts
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listZorgMailInboxFolders: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/zorgmail-inbox/folders`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Newest first by the server\'s arrival time. `search` narrows the page to messages whose subject, sender address or sender name contains it, case-insensitively. 
         * @summary One page of a folder\'s messages, newest first
         * @param {string} folderId A cached folder\&#39;s id
         * @param {string} [search] Text the subject, sender address or sender name must contain
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of messages per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listZorgMailInboxMessages: async (folderId: string, search?: string, page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'folderId' is not null or undefined
            assertParamExists('listZorgMailInboxMessages', 'folderId', folderId)
            const localVarPath = `/api/v1/zorgmail-inbox/folders/{folderId}/messages`
                .replace(`{${"folderId"}}`, encodeURIComponent(String(folderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (search !== undefined) {
                localVarQueryParameter['search'] = search;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Mark a message read, for the whole practice
         * @param {string} messageId A cached message\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markZorgMailInboxMessageSeen: async (messageId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'messageId' is not null or undefined
            assertParamExists('markZorgMailInboxMessageSeen', 'messageId', messageId)
            const localVarPath = `/api/v1/zorgmail-inbox/messages/{messageId}/seen`
                .replace(`{${"messageId"}}`, encodeURIComponent(String(messageId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Mark a message unread, for the whole practice
         * @param {string} messageId A cached message\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markZorgMailInboxMessageUnseen: async (messageId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'messageId' is not null or undefined
            assertParamExists('markZorgMailInboxMessageUnseen', 'messageId', messageId)
            const localVarPath = `/api/v1/zorgmail-inbox/messages/{messageId}/unseen`
                .replace(`{${"messageId"}}`, encodeURIComponent(String(messageId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Moved on the ZorgMail server, where only this one message is expunged from its folder. It leaves the cached folder at once and appears in the cached Prullenbak on the next sync. A message already in the Prullenbak is refused with `zorgmail-inbox-already-in-trash`: removing mail for good is not something Scrivio does (ADR-050 §2). 
         * @summary Move a message to the Prullenbak
         * @param {string} messageId A cached message\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        moveZorgMailInboxMessageToTrash: async (messageId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'messageId' is not null or undefined
            assertParamExists('moveZorgMailInboxMessageToTrash', 'messageId', messageId)
            const localVarPath = `/api/v1/zorgmail-inbox/messages/{messageId}/move-to-trash`
                .replace(`{${"messageId"}}`, encodeURIComponent(String(messageId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Submitted over SMTP **as the practice\'s mailbox** — never as the signed-in user, who is recorded in the audit log instead (who sent what to whom; never the body). Plain text only.  - **Reply** — `inReplyTo` names the cached message answered. `In-Reply-To` and   `References` are set from it, and its text is quoted below `body`. - **Forward** — `forwardOf` names the cached message forwarded. Its header block and text   follow `body`, and `forwardedAttachmentIndexes` picks which of its attachments go along;   each is malware-scanned first and refused when no clean verdict can be had. - **Dossier documents** — `documentIds` attaches documents from one cliënt\'s dossier, which   the caller must be allowed to download. Each is recorded as a download in the access log.  The message must fit ZorgMail\'s limit of 50 MB **as encoded on the wire**; one that does not is refused with `zorgmail-inbox-message-too-large` and nothing is sent.  Once ZorgMail has accepted the message a copy is appended to Verzonden, marked read, so it shows in the practice\'s other mail clients too. When that copy cannot be stored the send has still happened: the answer is `201` with `savedInSent: false` — \"verzonden, maar niet in Verzonden opgeslagen\" — never an error. 
         * @summary Send, reply or forward from the practice\'s mailbox
         * @param {SendZorgMailInboxMessageRequest} sendZorgMailInboxMessageRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendZorgMailInboxMessage: async (sendZorgMailInboxMessageRequest: SendZorgMailInboxMessageRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'sendZorgMailInboxMessageRequest' is not null or undefined
            assertParamExists('sendZorgMailInboxMessage', 'sendZorgMailInboxMessageRequest', sendZorgMailInboxMessageRequest)
            const localVarPath = `/api/v1/zorgmail-inbox/messages`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(sendZorgMailInboxMessageRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ZorgmailInboxApi - functional programming interface
 */
export const ZorgmailInboxApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ZorgmailInboxApiAxiosParamCreator(configuration)
    return {
        /**
         * Served only once the malware scan has called it clean (ADR-038). An infected attachment is refused with `zorgmail-inbox-attachment-infected`; when no verdict can be had the scan fails closed with `zorgmail-inbox-attachment-scan-unavailable`.  Always `application/octet-stream` with an attachment disposition; the attachment\'s own content type is on `ZorgMailInboxAttachment`. Every download is recorded in the access log. 
         * @summary One attachment\'s content
         * @param {string} messageId A cached message\&#39;s id
         * @param {number} index The attachment\&#39;s &#x60;index&#x60; on &#x60;ZorgMailInboxMessageDetail&#x60;
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async downloadZorgMailInboxAttachment(messageId: string, index: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.downloadZorgMailInboxAttachment(messageId, index, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgmailInboxApi.downloadZorgMailInboxAttachment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * \"Koppel aan cliënt\" (ADR-050 §6): copies the message into the chosen cliënt\'s dossier as a PDF of its headers and body, and each chosen attachment as a document of its own. The cliënt is the caller\'s choice; nothing is suggested. The message stays in the inbox.  Every file is type-checked from its bytes and malware-scanned before any is stored, so a refusal files nothing: an infected file is `zorgmail-inbox-attachment-infected`, no scan verdict is `zorgmail-inbox-attachment-scan-unavailable`. A message already filed into this cliënt\'s dossier is refused with `zorgmail-inbox-already-filed`.  Needs `zorgmail_inbox.read` and access to the cliënt\'s dossier. 
         * @summary File a message into a cliënt\'s dossier
         * @param {string} messageId A cached message\&#39;s id
         * @param {FileZorgMailInboxMessageRequest} fileZorgMailInboxMessageRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async fileZorgMailInboxMessage(messageId: string, fileZorgMailInboxMessageRequest: FileZorgMailInboxMessageRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgMailInboxFiling>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.fileZorgMailInboxMessage(messageId, fileZorgMailInboxMessageRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgmailInboxApi.fileZorgMailInboxMessage']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The headers, a plain-text body and — when the message has one — an HTML body that has been **sanitised on the server**: scripts, event handlers, forms, frames and styles that load anything are removed, and links open outside the app.  An image is never loaded from the sender\'s server — that is a read receipt and an IP leak. A remote image is replaced by an `<img data-zorgmail-blocked=\"remote\">` placeholder that has no `src`. An image the message carries itself (`cid:`) becomes `<img data-zorgmail-attachment=\"{index}\">`, which the client fills in from `downloadZorgMailInboxAttachment` with that index; a `cid:` that names no attachment is blocked like a remote one.  The first open downloads the message from ZorgMail. Opening does **not** mark it read. Every open is recorded in the access log. 
         * @summary One message, with its body
         * @param {string} messageId A cached message\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getZorgMailInboxMessage(messageId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgMailInboxMessageDetail>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getZorgMailInboxMessage(messageId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgmailInboxApi.getZorgMailInboxMessage']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * INBOX, Verzonden and Prullenbak, as far as the sync has cached them. Empty for a practice whose mailbox has not been synced yet, or has no mailbox login — which `mailboxConfigured` tells apart. `lastSyncError` is absent while the last sync succeeded. 
         * @summary The cached folders, with their unread counts
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listZorgMailInboxFolders(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgMailInboxFolderListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listZorgMailInboxFolders(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgmailInboxApi.listZorgMailInboxFolders']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Newest first by the server\'s arrival time. `search` narrows the page to messages whose subject, sender address or sender name contains it, case-insensitively. 
         * @summary One page of a folder\'s messages, newest first
         * @param {string} folderId A cached folder\&#39;s id
         * @param {string} [search] Text the subject, sender address or sender name must contain
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of messages per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listZorgMailInboxMessages(folderId: string, search?: string, page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgMailInboxMessagePage>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listZorgMailInboxMessages(folderId, search, page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgmailInboxApi.listZorgMailInboxMessages']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Mark a message read, for the whole practice
         * @param {string} messageId A cached message\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async markZorgMailInboxMessageSeen(messageId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.markZorgMailInboxMessageSeen(messageId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgmailInboxApi.markZorgMailInboxMessageSeen']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Mark a message unread, for the whole practice
         * @param {string} messageId A cached message\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async markZorgMailInboxMessageUnseen(messageId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.markZorgMailInboxMessageUnseen(messageId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgmailInboxApi.markZorgMailInboxMessageUnseen']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Moved on the ZorgMail server, where only this one message is expunged from its folder. It leaves the cached folder at once and appears in the cached Prullenbak on the next sync. A message already in the Prullenbak is refused with `zorgmail-inbox-already-in-trash`: removing mail for good is not something Scrivio does (ADR-050 §2). 
         * @summary Move a message to the Prullenbak
         * @param {string} messageId A cached message\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async moveZorgMailInboxMessageToTrash(messageId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.moveZorgMailInboxMessageToTrash(messageId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgmailInboxApi.moveZorgMailInboxMessageToTrash']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Submitted over SMTP **as the practice\'s mailbox** — never as the signed-in user, who is recorded in the audit log instead (who sent what to whom; never the body). Plain text only.  - **Reply** — `inReplyTo` names the cached message answered. `In-Reply-To` and   `References` are set from it, and its text is quoted below `body`. - **Forward** — `forwardOf` names the cached message forwarded. Its header block and text   follow `body`, and `forwardedAttachmentIndexes` picks which of its attachments go along;   each is malware-scanned first and refused when no clean verdict can be had. - **Dossier documents** — `documentIds` attaches documents from one cliënt\'s dossier, which   the caller must be allowed to download. Each is recorded as a download in the access log.  The message must fit ZorgMail\'s limit of 50 MB **as encoded on the wire**; one that does not is refused with `zorgmail-inbox-message-too-large` and nothing is sent.  Once ZorgMail has accepted the message a copy is appended to Verzonden, marked read, so it shows in the practice\'s other mail clients too. When that copy cannot be stored the send has still happened: the answer is `201` with `savedInSent: false` — \"verzonden, maar niet in Verzonden opgeslagen\" — never an error. 
         * @summary Send, reply or forward from the practice\'s mailbox
         * @param {SendZorgMailInboxMessageRequest} sendZorgMailInboxMessageRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async sendZorgMailInboxMessage(sendZorgMailInboxMessageRequest: SendZorgMailInboxMessageRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SendZorgMailInboxMessageResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.sendZorgMailInboxMessage(sendZorgMailInboxMessageRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgmailInboxApi.sendZorgMailInboxMessage']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ZorgmailInboxApi - factory interface
 */
export const ZorgmailInboxApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ZorgmailInboxApiFp(configuration)
    return {
        /**
         * Served only once the malware scan has called it clean (ADR-038). An infected attachment is refused with `zorgmail-inbox-attachment-infected`; when no verdict can be had the scan fails closed with `zorgmail-inbox-attachment-scan-unavailable`.  Always `application/octet-stream` with an attachment disposition; the attachment\'s own content type is on `ZorgMailInboxAttachment`. Every download is recorded in the access log. 
         * @summary One attachment\'s content
         * @param {string} messageId A cached message\&#39;s id
         * @param {number} index The attachment\&#39;s &#x60;index&#x60; on &#x60;ZorgMailInboxMessageDetail&#x60;
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadZorgMailInboxAttachment(messageId: string, index: number, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.downloadZorgMailInboxAttachment(messageId, index, options).then((request) => request(axios, basePath));
        },
        /**
         * \"Koppel aan cliënt\" (ADR-050 §6): copies the message into the chosen cliënt\'s dossier as a PDF of its headers and body, and each chosen attachment as a document of its own. The cliënt is the caller\'s choice; nothing is suggested. The message stays in the inbox.  Every file is type-checked from its bytes and malware-scanned before any is stored, so a refusal files nothing: an infected file is `zorgmail-inbox-attachment-infected`, no scan verdict is `zorgmail-inbox-attachment-scan-unavailable`. A message already filed into this cliënt\'s dossier is refused with `zorgmail-inbox-already-filed`.  Needs `zorgmail_inbox.read` and access to the cliënt\'s dossier. 
         * @summary File a message into a cliënt\'s dossier
         * @param {string} messageId A cached message\&#39;s id
         * @param {FileZorgMailInboxMessageRequest} fileZorgMailInboxMessageRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        fileZorgMailInboxMessage(messageId: string, fileZorgMailInboxMessageRequest: FileZorgMailInboxMessageRequest, options?: RawAxiosRequestConfig): AxiosPromise<ZorgMailInboxFiling> {
            return localVarFp.fileZorgMailInboxMessage(messageId, fileZorgMailInboxMessageRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * The headers, a plain-text body and — when the message has one — an HTML body that has been **sanitised on the server**: scripts, event handlers, forms, frames and styles that load anything are removed, and links open outside the app.  An image is never loaded from the sender\'s server — that is a read receipt and an IP leak. A remote image is replaced by an `<img data-zorgmail-blocked=\"remote\">` placeholder that has no `src`. An image the message carries itself (`cid:`) becomes `<img data-zorgmail-attachment=\"{index}\">`, which the client fills in from `downloadZorgMailInboxAttachment` with that index; a `cid:` that names no attachment is blocked like a remote one.  The first open downloads the message from ZorgMail. Opening does **not** mark it read. Every open is recorded in the access log. 
         * @summary One message, with its body
         * @param {string} messageId A cached message\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getZorgMailInboxMessage(messageId: string, options?: RawAxiosRequestConfig): AxiosPromise<ZorgMailInboxMessageDetail> {
            return localVarFp.getZorgMailInboxMessage(messageId, options).then((request) => request(axios, basePath));
        },
        /**
         * INBOX, Verzonden and Prullenbak, as far as the sync has cached them. Empty for a practice whose mailbox has not been synced yet, or has no mailbox login — which `mailboxConfigured` tells apart. `lastSyncError` is absent while the last sync succeeded. 
         * @summary The cached folders, with their unread counts
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listZorgMailInboxFolders(options?: RawAxiosRequestConfig): AxiosPromise<ZorgMailInboxFolderListResponse> {
            return localVarFp.listZorgMailInboxFolders(options).then((request) => request(axios, basePath));
        },
        /**
         * Newest first by the server\'s arrival time. `search` narrows the page to messages whose subject, sender address or sender name contains it, case-insensitively. 
         * @summary One page of a folder\'s messages, newest first
         * @param {string} folderId A cached folder\&#39;s id
         * @param {string} [search] Text the subject, sender address or sender name must contain
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of messages per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listZorgMailInboxMessages(folderId: string, search?: string, page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<ZorgMailInboxMessagePage> {
            return localVarFp.listZorgMailInboxMessages(folderId, search, page, size, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Mark a message read, for the whole practice
         * @param {string} messageId A cached message\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markZorgMailInboxMessageSeen(messageId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.markZorgMailInboxMessageSeen(messageId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Mark a message unread, for the whole practice
         * @param {string} messageId A cached message\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markZorgMailInboxMessageUnseen(messageId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.markZorgMailInboxMessageUnseen(messageId, options).then((request) => request(axios, basePath));
        },
        /**
         * Moved on the ZorgMail server, where only this one message is expunged from its folder. It leaves the cached folder at once and appears in the cached Prullenbak on the next sync. A message already in the Prullenbak is refused with `zorgmail-inbox-already-in-trash`: removing mail for good is not something Scrivio does (ADR-050 §2). 
         * @summary Move a message to the Prullenbak
         * @param {string} messageId A cached message\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        moveZorgMailInboxMessageToTrash(messageId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.moveZorgMailInboxMessageToTrash(messageId, options).then((request) => request(axios, basePath));
        },
        /**
         * Submitted over SMTP **as the practice\'s mailbox** — never as the signed-in user, who is recorded in the audit log instead (who sent what to whom; never the body). Plain text only.  - **Reply** — `inReplyTo` names the cached message answered. `In-Reply-To` and   `References` are set from it, and its text is quoted below `body`. - **Forward** — `forwardOf` names the cached message forwarded. Its header block and text   follow `body`, and `forwardedAttachmentIndexes` picks which of its attachments go along;   each is malware-scanned first and refused when no clean verdict can be had. - **Dossier documents** — `documentIds` attaches documents from one cliënt\'s dossier, which   the caller must be allowed to download. Each is recorded as a download in the access log.  The message must fit ZorgMail\'s limit of 50 MB **as encoded on the wire**; one that does not is refused with `zorgmail-inbox-message-too-large` and nothing is sent.  Once ZorgMail has accepted the message a copy is appended to Verzonden, marked read, so it shows in the practice\'s other mail clients too. When that copy cannot be stored the send has still happened: the answer is `201` with `savedInSent: false` — \"verzonden, maar niet in Verzonden opgeslagen\" — never an error. 
         * @summary Send, reply or forward from the practice\'s mailbox
         * @param {SendZorgMailInboxMessageRequest} sendZorgMailInboxMessageRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendZorgMailInboxMessage(sendZorgMailInboxMessageRequest: SendZorgMailInboxMessageRequest, options?: RawAxiosRequestConfig): AxiosPromise<SendZorgMailInboxMessageResponse> {
            return localVarFp.sendZorgMailInboxMessage(sendZorgMailInboxMessageRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ZorgmailInboxApi - object-oriented interface
 */
export class ZorgmailInboxApi extends BaseAPI {
    /**
     * Served only once the malware scan has called it clean (ADR-038). An infected attachment is refused with `zorgmail-inbox-attachment-infected`; when no verdict can be had the scan fails closed with `zorgmail-inbox-attachment-scan-unavailable`.  Always `application/octet-stream` with an attachment disposition; the attachment\'s own content type is on `ZorgMailInboxAttachment`. Every download is recorded in the access log. 
     * @summary One attachment\'s content
     * @param {string} messageId A cached message\&#39;s id
     * @param {number} index The attachment\&#39;s &#x60;index&#x60; on &#x60;ZorgMailInboxMessageDetail&#x60;
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public downloadZorgMailInboxAttachment(messageId: string, index: number, options?: RawAxiosRequestConfig) {
        return ZorgmailInboxApiFp(this.configuration).downloadZorgMailInboxAttachment(messageId, index, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * \"Koppel aan cliënt\" (ADR-050 §6): copies the message into the chosen cliënt\'s dossier as a PDF of its headers and body, and each chosen attachment as a document of its own. The cliënt is the caller\'s choice; nothing is suggested. The message stays in the inbox.  Every file is type-checked from its bytes and malware-scanned before any is stored, so a refusal files nothing: an infected file is `zorgmail-inbox-attachment-infected`, no scan verdict is `zorgmail-inbox-attachment-scan-unavailable`. A message already filed into this cliënt\'s dossier is refused with `zorgmail-inbox-already-filed`.  Needs `zorgmail_inbox.read` and access to the cliënt\'s dossier. 
     * @summary File a message into a cliënt\'s dossier
     * @param {string} messageId A cached message\&#39;s id
     * @param {FileZorgMailInboxMessageRequest} fileZorgMailInboxMessageRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public fileZorgMailInboxMessage(messageId: string, fileZorgMailInboxMessageRequest: FileZorgMailInboxMessageRequest, options?: RawAxiosRequestConfig) {
        return ZorgmailInboxApiFp(this.configuration).fileZorgMailInboxMessage(messageId, fileZorgMailInboxMessageRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The headers, a plain-text body and — when the message has one — an HTML body that has been **sanitised on the server**: scripts, event handlers, forms, frames and styles that load anything are removed, and links open outside the app.  An image is never loaded from the sender\'s server — that is a read receipt and an IP leak. A remote image is replaced by an `<img data-zorgmail-blocked=\"remote\">` placeholder that has no `src`. An image the message carries itself (`cid:`) becomes `<img data-zorgmail-attachment=\"{index}\">`, which the client fills in from `downloadZorgMailInboxAttachment` with that index; a `cid:` that names no attachment is blocked like a remote one.  The first open downloads the message from ZorgMail. Opening does **not** mark it read. Every open is recorded in the access log. 
     * @summary One message, with its body
     * @param {string} messageId A cached message\&#39;s id
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getZorgMailInboxMessage(messageId: string, options?: RawAxiosRequestConfig) {
        return ZorgmailInboxApiFp(this.configuration).getZorgMailInboxMessage(messageId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * INBOX, Verzonden and Prullenbak, as far as the sync has cached them. Empty for a practice whose mailbox has not been synced yet, or has no mailbox login — which `mailboxConfigured` tells apart. `lastSyncError` is absent while the last sync succeeded. 
     * @summary The cached folders, with their unread counts
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listZorgMailInboxFolders(options?: RawAxiosRequestConfig) {
        return ZorgmailInboxApiFp(this.configuration).listZorgMailInboxFolders(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Newest first by the server\'s arrival time. `search` narrows the page to messages whose subject, sender address or sender name contains it, case-insensitively. 
     * @summary One page of a folder\'s messages, newest first
     * @param {string} folderId A cached folder\&#39;s id
     * @param {string} [search] Text the subject, sender address or sender name must contain
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of messages per page
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listZorgMailInboxMessages(folderId: string, search?: string, page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return ZorgmailInboxApiFp(this.configuration).listZorgMailInboxMessages(folderId, search, page, size, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Mark a message read, for the whole practice
     * @param {string} messageId A cached message\&#39;s id
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public markZorgMailInboxMessageSeen(messageId: string, options?: RawAxiosRequestConfig) {
        return ZorgmailInboxApiFp(this.configuration).markZorgMailInboxMessageSeen(messageId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Mark a message unread, for the whole practice
     * @param {string} messageId A cached message\&#39;s id
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public markZorgMailInboxMessageUnseen(messageId: string, options?: RawAxiosRequestConfig) {
        return ZorgmailInboxApiFp(this.configuration).markZorgMailInboxMessageUnseen(messageId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Moved on the ZorgMail server, where only this one message is expunged from its folder. It leaves the cached folder at once and appears in the cached Prullenbak on the next sync. A message already in the Prullenbak is refused with `zorgmail-inbox-already-in-trash`: removing mail for good is not something Scrivio does (ADR-050 §2). 
     * @summary Move a message to the Prullenbak
     * @param {string} messageId A cached message\&#39;s id
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public moveZorgMailInboxMessageToTrash(messageId: string, options?: RawAxiosRequestConfig) {
        return ZorgmailInboxApiFp(this.configuration).moveZorgMailInboxMessageToTrash(messageId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Submitted over SMTP **as the practice\'s mailbox** — never as the signed-in user, who is recorded in the audit log instead (who sent what to whom; never the body). Plain text only.  - **Reply** — `inReplyTo` names the cached message answered. `In-Reply-To` and   `References` are set from it, and its text is quoted below `body`. - **Forward** — `forwardOf` names the cached message forwarded. Its header block and text   follow `body`, and `forwardedAttachmentIndexes` picks which of its attachments go along;   each is malware-scanned first and refused when no clean verdict can be had. - **Dossier documents** — `documentIds` attaches documents from one cliënt\'s dossier, which   the caller must be allowed to download. Each is recorded as a download in the access log.  The message must fit ZorgMail\'s limit of 50 MB **as encoded on the wire**; one that does not is refused with `zorgmail-inbox-message-too-large` and nothing is sent.  Once ZorgMail has accepted the message a copy is appended to Verzonden, marked read, so it shows in the practice\'s other mail clients too. When that copy cannot be stored the send has still happened: the answer is `201` with `savedInSent: false` — \"verzonden, maar niet in Verzonden opgeslagen\" — never an error. 
     * @summary Send, reply or forward from the practice\'s mailbox
     * @param {SendZorgMailInboxMessageRequest} sendZorgMailInboxMessageRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public sendZorgMailInboxMessage(sendZorgMailInboxMessageRequest: SendZorgMailInboxMessageRequest, options?: RawAxiosRequestConfig) {
        return ZorgmailInboxApiFp(this.configuration).sendZorgMailInboxMessage(sendZorgMailInboxMessageRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



